import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import { IItemCount } from '@educraft/base';

/**
 * Rozklad (Decomposition) - Number pyramid puzzles with images
 *
 * Layout: 3 portrait pages. Page 1: 2×2 grid, Page 2: 3×3 grid, Page 3: 4×4 grid.
 * Each cell contains a 3-row pyramid where some cells have numbers and others
 * have images. Bottom shows legend mapping images to their unknown values.
 *
 * Migrated from C# EduFrame.Img10.Generators.Rozklad
 */
export class Rozklad implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 3;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    this.generatePage(pdf, opts, 2);
    this.generatePage(pdf, opts, 3);
    this.generatePage(pdf, opts, 4);
  }

  private generatePage(pdf: PdfGen, opts: OptsImage, count: number): void {
    const page = pdf.addPage();
    const { origin, size } = calcOrigin(count, count, 0, PdfSize.A4);
    const sz = { width: size.width / 4, height: size.height / 8 };

    for (let x = 0; x < count; x++) {
      for (let y = 0; y < count; y++) {
        const rect = new PdfRect(
          origin.x + x * size.width,
          origin.y + y * size.height,
          size.width,
          size.height
        );
        const gen = this.genPyramid(opts);
        const images = Array.from({ length: opts.list.length }, (_, i) => i)
          .sort(() => opts.data.random.next(0, 2) - 1)
          .slice(0, gen.used.length);

        let index = 0;
        let usedIdx = 0;

        for (let y1 = 0; y1 < gen.data.length; y1++) {
          for (let x1 = 0; x1 < gen.data[y1].length; x1++) {
            const r = new PdfRect(
              rect.x + (rect.width - (y1 + 1) * sz.width) / 2 + x1 * sz.width,
              rect.y + rect.height / 8 + y1 * sz.height,
              sz.width,
              sz.height
            );
            page.drawRectangle(GUtil.basePen, null, r);

            if (gen.used.includes(index)) {
              page.drawFillStringCenter(
                gen.data[y1][x1].toString(),
                GUtil.baseFont,
                PdfBrush.Black,
                r
              );
            } else {
              const imgIdx = images[usedIdx];
              if (imgIdx !== undefined && imgIdx < opts.list.length) {
                page.drawImageCenter(opts.list[imgIdx].image, r);
              }
              usedIdx++;
            }

            index++;
          }
        }

        // Legend at bottom
        for (let i = 0; i < images.length; i++) {
          const r1 = new PdfRect(
            rect.x + sz.width,
            rect.y + (4.5 + i) * sz.height,
            sz.width,
            sz.height
          );
          const r2 = new PdfRect(r1.right, r1.y, sz.width, sz.height);

          const imgIdx = images[i];
          if (imgIdx !== undefined && imgIdx < opts.list.length) {
            page.drawImageCenter(opts.list[imgIdx].image, r1);
          }
          page.drawString('=', GUtil.baseFont, PdfBrush.Black, null, r2, ETextAlign.CenterLeft);
        }

        page.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }

  private genPyramid(opts: OptsImage): { data: number[][]; used: number[] } {
    const max = 20;
    let used: number[];
    let nums: number[];

    // Pick 3 positions from 6 to show as numbers (rest are images)
    do {
      used = Array.from({ length: 6 }, (_, i) => i)
        .sort(() => opts.data.random.next(0, 2) - 1)
        .slice(0, 3)
        .sort((a, b) => a - b);
    } while (
      this.arrEq(used, [0, 1, 2]) ||
      this.arrEq(used, [2, 4, 5]) ||
      this.arrEq(used, [1, 3, 4])
    );

    // Generate base numbers
    do {
      nums = Array.from({ length: 3 }, () => 1 + opts.data.random.next(0, max));
    } while (nums[0] + 2 * nums[1] + nums[2] > max);

    const data: number[][] = [
      [nums[0] + 2 * nums[1] + nums[2]],
      [nums[0] + nums[1], nums[1] + nums[2]],
      nums,
    ];

    return { data, used };
  }

  private arrEq(a: number[], b: number[]): boolean {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
}
